import { useEffect, useMemo, useState } from "react";
import { useFetch, useModal, useOptions, usePage, useResource } from "@/comn/hooks";
import { Process, TProcessItem } from "@/comn/components/Process";
import { COMN_APIS, T_PRCS_STTS_ITEM } from "./services/OsciLpcoComnAppService";

/** 신청 처리상태 공통코드. 취소 완료(CL) 단계의 이름을 여기서 가져온다. */
const APP_STAT_CD_GRP = "OSC0121";

export const APP_STAT_PPUP = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!

    const { paramData } = props;

    const [sttsItm, setSttsItm] = useState<TProcessItem[]>(T_PRCS_STTS_ITEM);

    /* 단계명은 프로세스 역할 코드(OSC0120)에서 오는데 CL 은 거기 없다.
       목록의 신청 상태 칸과 같은 이름이 나오도록 신청 처리상태 코드(OSC0121)에서 읽는다. */
    useResource({ defaultSchema: [{ area: "comnCd", comnCd: APP_STAT_CD_GRP }] });
    const { options: appStatOptions } = useOptions({ area: "comnCd", comnCd: APP_STAT_CD_GRP });

    const fetch = {
        getCfgnPrcsInfo: useFetch({
            // 신청 UUID 가 있으면 함께 보낸다. 서버가 신청 원본에서 신청서 코드와 신청 유형(신규/정정/취소)을
            // 판정하므로 화면이 넘긴 신청 유형이 틀려도 올바른 단계 구성을 받는다.
            api: () =>
                COMN_APIS.getCfgnPrcsInfo({
                    ...(paramData.appId ? { appId: paramData.appId } : {}),
                    appCdNo: paramData.appCdNo,
                    lpcoAplyTpCd: paramData.lpcoAplyTpCd,
                }),
            onSuccess: (data) => {
                const iconMap: Record<string, TProcessItem["icon"]> = {
                    SU: "start",
                    AP: "completed",
                };
                const list = Array.isArray(data.prcsList.content) ? data.prcsList.content : [];

                setSttsItm(
                    list.map((item: any) => ({
                        icon: iconMap[item.prcsRoleTpCd] ?? "inProgress",
                        title: item.prcsRoleTpNm,
                        prcsRoleTpCd: item.prcsRoleTpCd,
                        description: "                                 ",
                    })),
                );
                // 신청의 처리 역할 코드는 서버가 모든 행에 같은 값으로 내려주므로 첫 행에서 읽는다.
                setTimeout(() => handler.setInit(list[0]?.appPrcsRoleTpCd), 500);
            },
            onError: (error) => {
                const errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
    };

    const handler = {
        getCfgnPrcsInfo: () => {
            fetch.getCfgnPrcsInfo.fetch();
        },

        // 현재 단계 강조 기준
        // 1. 단계 목록에 신청 상태 코드가 있으면 지금까지와 같이 상태 코드로 비교한다.
        // 2. 없으면 신청의 처리 역할 코드로 비교한다. 상태 코드는 단계 코드 체계와 달라
        //    RC / RS / I1 / RE 처럼 단계 목록에 없는 값이 있고, 이 경우 강조가 사라진다.
        // 3. 둘 다 맞는 단계가 없으면 아무 단계도 강조하지 않는다(기존 동작 유지).
        // 순서를 반드시 지켜야 한다. 무조건 역할 코드로 비교하면 상태 SU(역할 VE) 건의 강조가
        // SU 에서 VE 로 옮겨가고, 역할 코드가 비어 있는 AP(승인 완료) 건은 강조가 사라진다.
        // 역할 코드는 서버가 신청 원본에서 읽어 준 값(appPrcsRoleTpCd)을 우선 사용하고, 없으면 화면이 넘긴 값을 쓴다.
        setInit: (appPrcsRoleTpCd?: string) => {
            const appStatCd = paramData.appStatCd;
            const prcsRoleTpCd = appPrcsRoleTpCd || paramData.prcsRoleTpCd;

            setSttsItm((prev) => {
                const hasAppStatCd = prev.some((item) => (item as any).prcsRoleTpCd === appStatCd);
                const currentCd = hasAppStatCd ? appStatCd : prcsRoleTpCd;

                if (!currentCd) return prev;

                return prev.map((item) =>
                    (item as any).prcsRoleTpCd === currentCd ? { ...item, current: true, color: "red" } : item,
                );
            });
        },
    };

    /*
     * 취소 완료(CL) 예외.
     * 취소 프로세스는 마지막 단계 AP(승인)로 끝나고, 그 승인이 곧 취소 완료라 신청 상태가 CL 이 된다.
     * CL 은 단계 코드에 없고 처리 역할 코드도 비어 있어 위 규칙으로는 어느 단계도 강조되지 않는다.
     * 그래서 상태가 CL 이고 마지막 단계가 AP 일 때만 그 자리를 CL 로 바꿔 강조한다. 단계 수는 그대로다.
     * 코드 이름은 비동기로 적재되므로 상태에 넣지 않고 그릴 때마다 최신 값으로 만든다.
     */
    const processItems = useMemo(() => {
        if (paramData.appStatCd !== "CL" || !sttsItm.length) return sttsItm;

        const last = sttsItm[sttsItm.length - 1] as any;
        if (last.prcsRoleTpCd !== "AP") return sttsItm;

        const clNm = appStatOptions.find((option) => option.value === "CL")?.label ?? last.title;
        return [...sttsItm.slice(0, -1), { ...last, prcsRoleTpCd: "CL", title: clNm, current: true, color: "red" }];
    }, [sttsItm, appStatOptions, paramData.appStatCd]);

    useEffect(() => {
        // handler.setInit();
        handler.getCfgnPrcsInfo();
    }, []);

    return <Process data={processItems} />;
};
